import React, { useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import Grid from "@mui/material/Grid"
import type { AxiosError, AxiosResponse } from "axios"
import type { MRT_PaginationState } from "material-react-table"
import moment from "moment"

import { RequestsLineGraph } from "../../../components/graphs/Line/RequestsLineGraph"
import { RequestLogTable } from "../../../components/tables/RequestLogTable"
import { KonanAPI } from "../../../services/KonanAPI"
import type { baseErrorType } from "../../../types/custom/projects"
import type { ListPredictions } from "../../../types/generated/api/ListPredictions"
import type { PaginatedListModelStateSwitchList } from "../../../types/generated/api/PaginatedListModelStateSwitchList"
import type { PaginatedListPredictionsList } from "../../../types/generated/api/PaginatedListPredictionsList"
import type { PaginatedModelListCreateList } from "../../../types/generated/api/PaginatedModelListCreateList"
import type { RequestSummaryDailyOutput } from "../../../types/generated/api/RequestSummaryDailyOutput"
import {
  convertActivityDataToMarkers,
  convertDatatoTotalRequestsLineGraph,
  fillMissingRequestSummaryDates,
} from "../../../utils/deploymentDetailsHelpers"
import { getModelByUUID } from "../../../utils/modelDetailsHelpers"

/**
 * Component that monitors requests/predictions on live model as well as evaluation metrics
 * contains requests log table with the (date, request body, response) to each request
 * @returns {React.ReactElement}
 */
export function ApiMonitor(): React.ReactElement {
  const { t } = useTranslation("liveMonitoring")
  const [searchParams] = useSearchParams()

  const { id: projectId } = useParams<{ id: string }>()

  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  // pagination state for requestLogTable
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // fetch project "/predictions/summary/daily/" endpoint
  const { isLoading: isLoadingDailyRequestSummary, data: dailyRequestSummaryResponse } =
    useQuery<RequestSummaryDailyOutput>(
      ["project-daily-request-summary", projectId, startDate, endDate],
      () =>
        KonanAPI.fetchDailyProjectRequestSummary({
          start_date: startDate as string,
          end_date: endDate as string,
          project_uuid: projectId as string,
        }),
      { enabled: !!startDate && !!endDate && !!projectId },
    )

  // fetch "/models" endpoint
  const { data: models } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError<baseErrorType>>(
    ["overviewModels", projectId],
    () => KonanAPI.fetchModels({ projectId: projectId as string, pageSize: 1000 }),
    {
      enabled: !!projectId,
    },
  )

  // fetch project "/switch/history/" endpoint
  const { isLoading: isActivityLoading, data: activityResponse } = useQuery<
    PaginatedListModelStateSwitchList,
    AxiosError<baseErrorType>
  >(["modelHistory", projectId], () => KonanAPI.getProjectHistory(projectId as string), {
    enabled: !!projectId,
  })

  // Will load request logs from the entire project
  const { isLoading: isLoadingRequestTableLog, data: requestLogTableResponse } = useQuery<
    AxiosResponse<PaginatedListPredictionsList>,
    AxiosError
  >(
    ["request-log", startDate, endDate, projectId, pagination.pageSize, pagination.pageIndex],
    () =>
      KonanAPI.fetchProjectRequestLogData(
        startDate as string,
        endDate as string,
        projectId as string,
        pagination.pageIndex + 1,
        pagination.pageSize,
      ),

    {
      keepPreviousData: true,
    },
  )

  //fetch dates that have data in them
  // TODO:: uncomment when ready from the backend
  // const { data: dates } = useQuery<string[], AxiosError<baseErrorType>>(["request-dates", projectId], () =>
  //   KonanAPI.getRequestDates(projectId as string)
  // )

  const memoizedGetModelById = useCallback(
    (currentModel: string) => getModelByUUID(currentModel, models?.data?.results),
    [models],
  )

  //add model name to activity response
  const activityData = useMemo(() => {
    if (activityResponse?.results && activityResponse?.results.length > 0) {
      return convertActivityDataToMarkers(memoizedGetModelById, activityResponse.results)
    }
    return []
  }, [activityResponse?.results, memoizedGetModelById])

  // memoizing the returned data
  const adjustedData = useMemo(() => {
    if (startDate && endDate) {
      // If there's new data, fill in the missing dates and convert it to Serie[] type
      return convertDatatoTotalRequestsLineGraph(
        fillMissingRequestSummaryDates(
          moment(startDate),
          moment(endDate),
          dailyRequestSummaryResponse?.data ?? [],
          activityData,
        ),
      )
    }
    return []
  }, [startDate, endDate, dailyRequestSummaryResponse, activityData])

  return (
    <Grid container spacing={2}>
      {/* Requests Line Graph */}
      <Grid item xs={12}>
        <RequestsLineGraph
          title={t("ApiMonitor.charts.requestsPerDay")}
          data={adjustedData}
          graphHeight={400}
          isLoading={isLoadingDailyRequestSummary || isActivityLoading}
        />
      </Grid>
      <Grid item xs={12}>
        {/* 
            Requests log table
            Will display request logs from entire project since modelId is not supplied and is given the appropriate data.
        */}
        <RequestLogTable
          data={requestLogTableResponse?.data?.results as ListPredictions}
          rowCount={requestLogTableResponse?.data?.count}
          isFetching={isLoadingRequestTableLog}
          pagination={pagination}
          setPagination={setPagination}
          modelName={memoizedGetModelById}
        />
      </Grid>
    </Grid>
  )
}
